import React, { useEffect, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Loader2, Upload } from 'lucide-react';
import jsQR from 'jsqr';

interface PortalGuruScannerPresensiProps {
    onSuccess: (lokasiId: string) => void;
    onClose: () => void;
}

export const PortalGuruScannerPresensi: React.FC<PortalGuruScannerPresensiProps> = ({ onSuccess, onClose }) => {
    const [error, setError] = useState<string | null>(null);
    const [isScanning, setIsScanning] = useState(false);

    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsScanning(true);
        setError(null);

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                if (!context) {
                    setError("Gagal memproses gambar.");
                    setIsScanning(false);
                    return;
                }

                canvas.width = img.width;
                canvas.height = img.height;
                context.drawImage(img, 0, 0, img.width, img.height);

                const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, imageData.width, imageData.height);

                if (code) {
                    try {
                        const data = JSON.parse(code.data);
                        if (data && data.type === 'PRESENSI' && data.id) {
                            onSuccess(data.id);
                        } else {
                            setError("QR Code tidak valid untuk presensi.");
                            setIsScanning(false);
                        }
                    } catch (err) {
                        setError("Format QR Code tidak dikenali.");
                        setIsScanning(false);
                    }
                } else {
                    setError("QR Code tidak ditemukan pada gambar.");
                    setIsScanning(false);
                }
            };
            img.onerror = () => {
                setError("Gagal membaca file gambar.");
                setIsScanning(false);
            };
            img.src = event.target?.result as string;
        };
        reader.onerror = () => {
            setError("Gagal membaca file.");
            setIsScanning(false);
        };
        reader.readAsDataURL(file);
    };


    useEffect(() => {
        let html5QrCode: Html5Qrcode;

        const startScanner = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                stream.getTracks().forEach(track => track.stop()); // Just to check permission

                html5QrCode = new Html5Qrcode("reader");
                setIsScanning(true);
                await html5QrCode.start(
                    { facingMode: "environment" },
                    {
                        fps: 10,
                        qrbox: { width: 250, height: 250 }
                    },
                    (decodedText) => {
                        try {
                            const data = JSON.parse(decodedText);
                            if (data && data.type === 'PRESENSI' && data.id) {
                                html5QrCode.stop().then(() => {
                                    setIsScanning(false);
                                    onSuccess(data.id);
                                });
                            } else {
                                setError("QR Code tidak valid untuk presensi.");
                            }
                        } catch (e) {
                            setError("Format QR Code tidak dikenali.");
                        }
                    },
                    (errorMessage) => {
                        // ignore background scanning errors
                    }
                );
            } catch (err: any) {
                console.error("Error starting scanner:", err);
                setError(err.message || "Gagal mengakses kamera. Pastikan izin kamera diberikan.");
                setIsScanning(false);
            }
        };

        startScanner();

        return () => {
            if (html5QrCode && html5QrCode.isScanning) {
                html5QrCode.stop().catch(console.error);
            }
        };
    }, [onSuccess]);

    return (
        <div className="fixed inset-0 z-[120] bg-black flex flex-col">
            <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/80 to-transparent">
                <button onClick={onClose} className="p-2 rounded-full bg-white/20 text-white backdrop-blur-md">
                    <X size={24} />
                </button>
                <span className="text-white font-medium text-sm">Scan QR Presensi</span>
                <div className="w-10"></div> {/* Spacer */}
            </div>

            <div className="flex-1 flex flex-col items-center justify-center relative bg-black">
                {!isScanning && !error && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
                        <Loader2 className="animate-spin text-emerald-500" size={48} />
                    </div>
                )}

                {error && (
                    <div className="absolute inset-x-4 top-20 bg-red-500/90 backdrop-blur-md text-white p-4 rounded-2xl text-center text-sm z-30 shadow-2xl animate-in slide-in-from-top fade-in">
                        <p className="font-bold mb-1">Gagal Scan</p>
                        <p className="text-red-100">{error}</p>
                        <button onClick={() => setError(null)} className="mt-3 px-4 py-1.5 bg-white/20 rounded-full text-xs font-semibold hover:bg-white/30 transition-colors">
                            Tutup Pesan
                        </button>
                    </div>
                )}

                <div id="reader" className="w-full max-w-md overflow-hidden bg-black"></div>
            </div>

            <div className="absolute bottom-0 inset-x-0 p-8 bg-gradient-to-t from-black via-black/80 to-transparent flex flex-col items-center text-center">
                <p className="text-white/90 text-sm font-medium">Arahkan kamera ke QR Code Presensi</p>
                <p className="text-white/50 text-xs mt-2">Pastikan QR Code berada dalam kotak area scan</p>
                <div className="mt-6 flex flex-col items-center w-full z-30 relative">
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept="image/*"
                        className="hidden"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 px-6 py-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full text-white text-sm font-medium transition-all"
                    >
                        <Upload size={18} />
                        <span>Upload Foto QR</span>
                    </button>
                    <p className="text-white/50 text-xs mt-3">Kamera tidak bisa dibuka? Upload foto QR Code</p>
                </div>
            </div>
        </div>
    );
};
